import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import type { Entity } from '../state/store';
import { useAppStore } from '../state/store';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { useAnnotationInk } from '../hooks/useThemeInk';
import { BACKFACE_DEFINES } from './backfaceTint';

/**
 * Renders a command-produced solid/surface payload (vertices+indices).
 * Used by EntityLayer's 'solid' branch and CadReferenceLayer so geometry
 * results of primitives, booleans, extrudes, lofts and surface commands
 * display uniformly in every workbench. Z-up world; position offset comes
 * from data.position.
 */
export const SolidEntityMesh = memo(function SolidEntityMesh({
    entity,
    isSelected,
    isHovered,
}: {
    entity: Entity;
    isSelected: boolean;
    isHovered: boolean;
}) {
    const bodyColor = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || '#8fb4d9';
    // Wireframe strokes follow the theme ink instead of the body fallback:
    // the light steel-blue body tone is unreadable on the light viewport.
    const ink = useAnnotationInk();
    const strokeColor = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : (entity.data.color as string) || ink;
    const wire = !!entity.data.wire;

    const geometry = useMemo(() => {
        const vertices = (entity.data.vertices as number[][]) || [];
        const indices = (entity.data.indices as number[][]) || [];
        if (vertices.length === 0) return null;
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(vertices.length * 3);
        vertices.forEach((v, i) => {
            pos[i * 3] = v[0];
            pos[i * 3 + 1] = v[1];
            pos[i * 3 + 2] = v[2] ?? 0;
        });
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const idxArr: number[] = [];
        indices.forEach((t) => { if (t.length === 3) idxArr.push(t[0], t[1], t[2]); });
        if (idxArr.length > 0) geo.setIndex(idxArr);
        geo.computeVertexNormals();
        return geo;
    }, [entity.data.vertices, entity.data.indices]);

    // Workbench display mode (R6-2): the shared renderer obeys
    // wireframe/shaded/xray/ghosted for general (CAD/3D) geometry.
    const viewMode = useAppStore((s) => s.view.viewMode);
    const asWireframe = viewMode === 'wireframe' || viewMode === 'hiddenLine';
    const opacity = isSelected ? 1
        : viewMode === 'xray' ? 0.25
            : viewMode === 'ghosted' ? 0.35
                : 0.92;

    const refCallback = useEntityRegistryRef(entity.id, entity.layer);

    if (!geometry) return null;

    const position = (entity.data.position as [number, number, number]) || [0, 0, 0];
    const rotation = (entity.data.rotation as [number, number, number]) || [0, 0, 0];

    const handleClick = (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        dispatchEntityClick(entity.id, e as any);
    };

    if (wire || asWireframe) {
        // Surface-style payload (or the workbench wireframe display mode):
        // draw the quad/triangle edges as wireframe.
        const edges = useMemo(() => {
            const seen = new Set<string>();
            const pts: number[][] = [];
            const indices = (entity.data.indices as number[][]) || [];
            const verts = (entity.data.vertices as number[][]) || [];
            indices.forEach(([a, b, c]) => {
                const pairs: [number, number][] = [[a, b], [b, c], [c, a]];
                pairs.forEach(([x, y]) => {
                    const key = x < y ? `${x}-${y}` : `${y}-${x}`;
                    if (seen.has(key)) return;
                    seen.add(key);
                    pts.push(verts[x], verts[y]);
                });
            });
            return pts;
        }, [entity.data]);
        if (edges.length < 2) return null;
        return (
            <Line
                ref={refCallback as any}
                points={edges as [number, number, number][]}
                color={strokeColor}
                lineWidth={isSelected ? 2 : 1}
                position={position}
                onClick={handleClick}
            />
        );
    }

    return (
        <mesh
            ref={refCallback}
            geometry={geometry}
            position={position}
            rotation={rotation}
            onClick={handleClick}
            castShadow
            receiveShadow
        >
            <meshStandardMaterial
                color={bodyColor}
                roughness={0.6}
                metalness={0.1}
                side={THREE.DoubleSide}
                defines={BACKFACE_DEFINES}
                transparent
                opacity={opacity}
            />
        </mesh>
    );
});
